import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["data-ad-client", "data-ad-slot"]
const _hoisted_2 = ["data-ad-client", "data-ad-slot"]
const _hoisted_3 = ["data-ad-client", "data-ad-slot"]
const _hoisted_4 = ["data-ad-client", "data-ad-slot"]
const _hoisted_5 = {
  key: 1,
  class: "text-xs text-gray-400 text-center mt-2"
}

import { ref, computed, onMounted, nextTick, onUnmounted } from 'vue';
import { useConsentStore } from '@/stores/consentStore';
import { adSenseConfig } from '@/services/adSenseConfig';

// Define emits
interface Props {
  adType: 'square' | 'infeed' | 'banner' | 'rectangle';
  className?: string;
  showLabel?: boolean;
  minContentHeight?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GoogleAd',
  props: {
    adType: {},
    className: { default: '' },
    showLabel: { type: Boolean, default: true },
    minContentHeight: { default: 300 }
  },
  emits: ["ad-loaded", "ad-failed"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const props = __props;

// State
const adContainer = ref<HTMLElement | null>(null);
const adLoaded = ref(false);
const isLoading = ref(false);
const loadTimeout = ref<number | null>(null);

// Composables
const consentStore = useConsentStore();

// Computed
const shouldShowAd = computed(() => {
  // Only show ads if user has consented to advertising
  if (!consentStore.canUseAdvertising) {
    return false;
  }
  
  // Check if page has sufficient content
  const contentHeight = document.documentElement.scrollHeight;
  if (contentHeight < props.minContentHeight) {
    return false;
  }
  
  return true;
});

const containerClasses = computed(() => [
  'google-ad-container',
  props.className,
  {
    'ad-loaded': adLoaded.value,
    'ad-loading': isLoading.value
  }
]);

const loadingClasses = computed(() => {
  const baseClasses = 'ad-loading-placeholder';
  switch (props.adType) {
    case 'banner':
      return `${baseClasses} h-20 w-full`;
    case 'rectangle':
      return `${baseClasses} h-64 w-80 mx-auto`;
    case 'square':
      return `${baseClasses} h-64 w-64 mx-auto`;
    case 'infeed':
      return `${baseClasses} h-32 w-full`;
    default:
      return `${baseClasses} h-20 w-full`;
  }
});

// Methods
const loadAd = async () => {
  if (!shouldShowAd.value || !adContainer.value) {
    return;
  }

  isLoading.value = true;

  // Set a timeout to hide loading state after 3 seconds
  loadTimeout.value = setTimeout(() => {
    isLoading.value = false;
    if (!adLoaded.value) {
      console.log(`Ad load timeout for ${props.adType}`);
      emit('ad-failed');
    }
  }, 3000);

  try {
    await nextTick();
    
    // Check if container has width before loading ad
    const containerWidth = adContainer.value.offsetWidth;
    if (containerWidth === 0) {
      console.log(`Skipping ad load - container width is 0 for ${props.adType}`);
      isLoading.value = false;
      return;
    }
    
    // Ensure adsbygoogle array exists
    if (typeof window !== 'undefined') {
      const adWindow = window as Window & {
        adsbygoogle?: Array<Record<string, unknown>> & { loaded?: boolean };
      };
      const adsbygoogle = adWindow.adsbygoogle || [];

      adWindow.adsbygoogle = adsbygoogle;

      // Push the ad
      adsbygoogle.push({});
      
      // Mark as loaded and clear loading state
      adLoaded.value = true;
      isLoading.value = false;
      
      // Clear timeout since ad loaded successfully
      if (loadTimeout.value) {
        clearTimeout(loadTimeout.value);
        loadTimeout.value = null;
      }
      
      // Emit success event
      emit('ad-loaded');
      
      console.log(`Google Ad loaded: ${props.adType}`);
    }
  } catch (error) {
    console.error('Error loading Google Ad:', error);
    isLoading.value = false;
    emit('ad-failed');
  }
};

// Cleanup function
const cleanup = () => {
  if (loadTimeout.value) {
    clearTimeout(loadTimeout.value);
    loadTimeout.value = null;
  }
};

// Lifecycle
onMounted(async () => {
  if (shouldShowAd.value) {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      loadAd();
    }, 100);
  }
});

// Cleanup on unmount
onUnmounted(() => {
  cleanup();
});

return (_ctx: any,_cache: any) => {
  return (shouldShowAd.value && (adLoaded.value || isLoading.value))
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(containerClasses.value),
        role: "complementary",
        "aria-label": "Advertisement"
      }, [
        (isLoading.value && !adLoaded.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(loadingClasses.value)
            }, _cache[0] || (_cache[0] = [
              _createElementVNode("div", { class: "animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center" }, [
                _createElementVNode("span", { class: "text-xs text-gray-500 dark:text-gray-400" }, "Loading ad...")
              ], -1)
            ]), 2))
          : _createCommentVNode("", true),
        _withDirectives(_createElementVNode("div", null, [
          (_ctx.adType === 'square')
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                ref_key: "adContainer",
                ref: adContainer
              }, [
                _createElementVNode("ins", {
                  class: "adsbygoogle",
                  style: {"display":"block"},
                  "data-ad-client": _unref(adSenseConfig).getClientId(),
                  "data-ad-slot": _unref(adSenseConfig).getAdSlot('square'),
                  "data-ad-format": "auto",
                  "data-full-width-responsive": "true"
                }, null, 8, _hoisted_1)
              ], 512))
            : (_ctx.adType === 'infeed')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  ref_key: "adContainer",
                  ref: adContainer
                }, [
                  _createElementVNode("ins", {
                    class: "adsbygoogle",
                    style: {"display":"block"},
                    "data-ad-format": "autorelaxed",
                    "data-ad-client": _unref(adSenseConfig).getClientId(),
                    "data-ad-slot": _unref(adSenseConfig).getAdSlot('infeed')
                  }, null, 8, _hoisted_2)
                ], 512))
              : (_ctx.adType === 'banner')
                ? (_openBlock(), _createElementBlock("div", {
                    key: 2,
                    ref_key: "adContainer",
                    ref: adContainer
                  }, [
                    _createElementVNode("ins", {
                      class: "adsbygoogle",
                      style: {"display":"block"},
                      "data-ad-client": _unref(adSenseConfig).getClientId(),
                      "data-ad-slot": _unref(adSenseConfig).getAdSlot('banner'),
                      "data-ad-format": "auto",
                      "data-full-width-responsive": "true"
                    }, null, 8, _hoisted_3)
                  ], 512))
                : (_ctx.adType === 'rectangle')
                  ? (_openBlock(), _createElementBlock("div", {
                      key: 3,
                      ref_key: "adContainer",
                      ref: adContainer
                    }, [
                      _createElementVNode("ins", {
                        class: "adsbygoogle",
                        style: {"display":"block"},
                        "data-ad-client": _unref(adSenseConfig).getClientId(),
                        "data-ad-slot": _unref(adSenseConfig).getAdSlot('rectangle'),
                        "data-ad-format": "auto",
                        "data-full-width-responsive": "true"
                      }, null, 8, _hoisted_4)
                    ], 512))
                  : _createCommentVNode("", true)
        ], 512), [
          [_vShow, !isLoading.value || adLoaded.value]
        ]),
        (_ctx.showLabel && (adLoaded.value || !isLoading.value))
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Advertisement "))
          : _createCommentVNode("", true)
      ], 2))
    : _createCommentVNode("", true)
}
}

})